import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Template, attachmentProblem, deriveAnswers, isItemVisible, returnedEditableRefs, type Answers, type ControlAnswer, type Section } from '@sss/form-engine';
import type { CarriedFinding, PackPerson } from '../../lib/api/types';
import { compressMedia } from '../../lib/media/compress';
import { positionOrNull } from '../../lib/native';
import { useI18n } from '../../lib/i18n';
import { PACK_PEOPLE_META } from '../../lib/sync/pack';
import { useDraft, useOnline, usePackSite, useTemplateVersion } from '../../app/hooks';
import { useServices } from '../../app/services';
import { useToast } from '../../components/Toast';
import { applyControl, applyField, DRAFT_NOT_FOUND, lookupValuesFor, surveyContextFor, withAttachmentsAdded, withoutAttachment } from './draft-service';
import type { CaptureActions, CaptureLookup } from './types';

const SHOW_ENGLISH_KEY = 'sss.capture.showEnglish';

/**
 * Everything a capture screen needs for one draft: the draft (with answers
 * held optimistically in memory), its pinned template, site context, display
 * language and bound actions.
 */
export function useCapture(draftId: string | undefined) {
  const { drafts, storage, api } = useServices();
  const { lang, t } = useI18n();
  const toast = useToast();
  const online = useOnline();
  const [people, setPeople] = useState<PackPerson[]>([]);
  const draftQ = useDraft(draftId);
  const draft = draftQ.data;
  const tplQ = useTemplateVersion(draft?.templateVersionId);
  const siteQ = usePackSite(draft?.siteId);
  const [answers, setAnswers] = useState<Answers | null>(null);
  const [carried, setCarried] = useState<CarriedFinding[]>([]);
  const [showEnglish, setShowEnglishState] = useState(() => localStorage.getItem(SHOW_ENGLISH_KEY) === '1');

  // Answers are owned by this hook once loaded (optimistic UI); storage catches up serially.
  useEffect(() => {
    setAnswers(null);
  }, [draftId]);
  useEffect(() => {
    if (draft && answers === null) setAnswers(draft.answers);
  }, [draft, answers]);

  useEffect(() => {
    if (draft?.siteId) storage.findingsForSite(draft.siteId).then(setCarried, () => setCarried([]));
  }, [draft?.siteId, storage]);
  useEffect(() => {
    storage.getMeta<PackPerson[]>(PACK_PEOPLE_META).then((p) => setPeople(p ?? []), () => setPeople([]));
  }, [storage]);

  const template = useMemo(() => {
    const def = tplQ.data?.definition;
    if (!def) return null;
    const r = Template.safeParse(def);
    return r.success ? r.data : null;
  }, [tplQ.data]);

  const site = siteQ.data;
  const context = useMemo(() => surveyContextFor(site), [site]);
  const displayLang = showEnglish ? 'en' : lang;

  const setShowEnglish = (v: boolean) => {
    localStorage.setItem(SHOW_ENGLISH_KEY, v ? '1' : '0');
    setShowEnglishState(v);
  };

  // Every change re-derives lookups and calculated fields, on screen and in storage (C1, C6).
  const lookups = useMemo(() => (site ? lookupValuesFor(site) : {}), [site]);
  const derive = useCallback(
    (a: Answers): Answers => (template ? deriveAnswers(template, a, lookups) : a),
    [template, lookups],
  );

  // Changes made on this screen; a reload that overlaps one starts again, so it never takes it back off the screen.
  const edits = useRef(0);

  /** Show what is really stored (after a failed local save, a submit that found problems, or a change made in the background). */
  const reload = useCallback(() => {
    if (!draftId) return;
    const run = () => {
      const at = edits.current;
      void drafts
        .settled(draftId)
        .then(() => drafts.get(draftId))
        .then(
          (d) => {
            if (edits.current !== at) return run();
            if (d) setAnswers(d.answers);
          },
          () => {},
        );
    };
    run();
  }, [drafts, draftId]);

  // Storage replaced the answers in the background (C2-1): the server's copy of a survey
  // given back after reassignment, a survey the analyst returned, a refused file taken out.
  // Every keystroke saves the draft too, so only these show in the signature.
  const scans = draft
    ? Object.values(draft.answers.controls)
        .map((c) => (c.checkpointScan ? `${c.checkpointScan.scanId}:${c.checkpointScan.status}` : ''))
        .join('')
    : '';
  const replaced = draft ? `${draft.status}|${draft.withdrawn?.reason ?? ''}|${draft.refusedFiles?.length ?? 0}|${scans}` : null;
  const seen = useRef<string | null>(null);
  useEffect(() => {
    seen.current = null;
  }, [draftId]);
  useEffect(() => {
    if (replaced === null) return;
    const before = seen.current;
    seen.current = replaced;
    if (before !== null && before !== replaced) reload();
  }, [replaced, reload]);

  // A change the device could not store (storage full, cipher error) must not look saved.
  // A draft discarded meanwhile has nothing left to save, and nothing to report.
  const saveFailed = useCallback(
    (e?: unknown) => {
      if (e instanceof Error && e.message === DRAFT_NOT_FOUND) return;
      toast.show(t('ui.capture.saveFailed'), 'danger');
      reload();
    },
    [toast, t, reload],
  );

  const setField = useCallback(
    (key: string, value: unknown) => {
      if (!draftId) return;
      edits.current++;
      setAnswers((a) => (a ? derive(applyField(a, key, value)) : a));
      drafts.update(draftId, (a) => derive(applyField(a, key, value))).catch(saveFailed);
    },
    [drafts, draftId, derive, saveFailed],
  );

  const setControl = useCallback(
    (code: string, patch: Partial<ControlAnswer>) => {
      if (!draftId) return;
      edits.current++;
      setAnswers((a) => (a ? applyControl(a, code, patch) : a));
      drafts.setControl(draftId, code, patch).catch(saveFailed);
    },
    [drafts, draftId, saveFailed],
  );

  const actions: CaptureActions = useMemo(
    () => ({
      addFiles: async (target, kind, files) => {
        if (!draftId) return [];
        const ref = 'control' in target ? target.control : target.field;
        const ids: string[] = [];
        let failed = false;
        let error: unknown;
        try {
          const pos = kind === 'PHOTO' ? await positionOrNull() : null;
          for (const f of files) {
            const blob = kind !== 'SIGNATURE' ? await compressMedia(f).catch(() => f) : f;
            // Never queue a file the server will refuse: it would be lost later, offline (C5).
            const problem = attachmentProblem(kind, blob.type || 'application/octet-stream', blob.size);
            if (problem) {
              toast.show(t(`ui.capture.fileRefused.${problem}`, { name: f instanceof File ? f.name : '' }), 'danger');
              continue;
            }
            const a = await drafts.addAttachment(draftId, ref, kind, blob, {
              fileName: f instanceof File ? f.name : undefined,
              lat: pos?.lat,
              lng: pos?.lng,
            });
            ids.push(a.id);
          }
        } catch (e) {
          failed = true;
          error = e;
        }
        if (ids.length > 0) {
          // GPS and compression take a while: add to the answers as they are now, not as they were when the add started.
          const add = (a: Answers) => derive(withAttachmentsAdded(a, target, kind, ids));
          edits.current++;
          setAnswers((a) => (a ? add(a) : a));
          await drafts.update(draftId, add).catch((e) => {
            failed = true;
            error = e;
          });
        }
        if (failed) saveFailed(error);
        return ids;
      },
      removeFile: async (id) => {
        if (!draftId) return;
        edits.current++;
        setAnswers((a) => (a ? withoutAttachment(a, id) : a));
        try {
          await drafts.update(draftId, (a) => withoutAttachment(a, id));
          await drafts.removeAttachment(id);
        } catch (e) {
          saveFailed(e);
        }
      },
      // Surveys restored from the server (new phone, returned survey) have no local copy: read it online (C23).
      fileUrl: async (id) => {
        const local = await drafts.attachmentUrl(id);
        if (local || !online) return local;
        return api.blob(`/attachments/${encodeURIComponent(id)}`).then((b) => URL.createObjectURL(b), () => null);
      },
      scanCheckpoint: async (code, scan) => {
        if (!draftId) return;
        const pos = await positionOrNull(6_000);
        edits.current++;
        try {
          const entry = await drafts.recordCheckpointScan(draftId, code, { ...scan, lat: pos?.lat, lng: pos?.lng, accuracy: pos?.accuracy });
          setAnswers((a) => (a ? applyControl(a, code, { checkpointScan: entry }) : a));
        } catch (e) {
          saveFailed(e);
        }
      },
      fileMeta: async (id) => {
        const a = await storage.getAttachment(id);
        return a ? { contentType: a.contentType, fileName: a.fileName } : null;
      },
    }),
    [drafts, draftId, toast, t, online, api, storage, derive, saveFailed],
  );

  const lookup: CaptureLookup = { siteName: site?.name, customerName: site?.customerName, branch: site?.branchCode, site, people };

  const liveDraft = draft && answers ? { ...draft, answers } : draft;
  const visibleSections: Section[] = useMemo(() => {
    if (!template || !liveDraft) return [];
    const scope = { answers: liveDraft.answers, context };
    return template.sections.filter((s) => s.items.some((it) => isItemVisible(s, it, scope)));
  }, [template, liveDraft, context]);

  // A returned survey: flagged items plus the questions their answers reveal (C11).
  const editableRefs = useMemo(
    () => (template && draft?.status === 'RETURNED' ? returnedEditableRefs(template, Object.keys(draft.flags)) : null),
    [template, draft?.status, draft?.flags],
  );

  return {
    editableRefs,
    loading: draftQ.loading || (!!draft && (tplQ.loading || answers === null)),
    draft: liveDraft,
    template,
    templateMissing: !!draft && !tplQ.loading && !template,
    site,
    context,
    carried,
    displayLang,
    showEnglish,
    setShowEnglish,
    setField,
    setControl,
    reload,
    actions,
    lookup,
    visibleSections,
  };
}

/** Questions are numbered across the whole survey; this gives a section's first number. */
export function controlNumberOffset(template: Template, sections: Section[], code: string, answers: Answers, context: ReturnType<typeof surveyContextFor>): number {
  let n = 0;
  for (const s of sections) {
    if (s.code === code) return n;
    n += s.items.filter((it) => it.kind === 'control' && isItemVisible(s, it, { answers, context })).length;
  }
  return n;
}
